import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { paymentMethodChannel } from "@/lib/payment-methods";
import { resolveMyTcId } from "@/lib/api/tc-context";


/**
 * Lista cobranças pendentes geradas a partir de contratos.
 * Antes de listar, reconcilia: para cada aluno com contrato ativo cuja forma
 * de pagamento seja Pix com comprovante e que ainda NÃO possua cobrança
 * pendente, cria uma automaticamente. Assim o card aparece imediatamente
 * mesmo para contratos criados antes de a lógica existir.
 */
export const listCharges = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ view: z.enum(["pendentes", "inativas"]).optional() }).optional().parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    const view = data?.view ?? "pendentes";
    const tcId = await resolveMyTcId(context.supabase, context.userId);


    // 1) Alunos com contrato ativo em Pix-comprovante
    const { data: eligibleStudents, error: sErr } = await context.supabase
      .from("students")
      .select("id, name, payment_method, plan_discount, plan_price_id, plan_start_date, plan_end_date, plan_prices(plan_type, price, due_type, due_offset_days, due_fixed_day), parents(name, phone, cpf)")
      .eq("training_center_id", tcId)
      .not("plan_price_id", "is", null);
    if (sErr) throw sErr;

    // Todo contrato Pix (qualquer variante) gera cobrança na tela: Pix
    // com comprovante, Pix presencial e Pix online (Mercado Pago). Assim
    // é possível enviar copia-e-cola/lembrete via WhatsApp em qualquer caso.
    const pixStudents = (eligibleStudents ?? []).filter((s: any) => {
      const m = String(s.payment_method || "");
      return m === "pix_comprovante" || m === "pix_presencial" || m === "pix";
    });

    // Cobranças já existentes (aguardando, inativas OU confirmadas nos últimos 25 dias)
    // — evita duplicar cobrança do mesmo aluno logo após um pagamento confirmado
    // e evita recriar cobrança que foi inativada manualmente.
    const cutoff = new Date(Date.now() - 25 * 24 * 3600 * 1000).toISOString();
    const { data: existing, error: eErr } = await context.supabase
      .from("pix_payment_proofs")
      .select("linked_id, status, created_at")
      .eq("training_center_id", tcId)
      .eq("source", "contract")
      .eq("linked_kind", "student")
      .or(`status.eq.aguardando,status.eq.inativo,and(status.eq.confirmado,created_at.gte.${cutoff})`);
    if (eErr) throw eErr;
    const withCharge = new Set((existing ?? []).map((r: any) => r.linked_id));



    // 3) Cria as faltantes (só na visão de pendentes)
    const missing = view === "pendentes" ? pixStudents.filter((s: any) => !withCharge.has(s.id)) : [];
    if (missing.length > 0) {
      const rows = missing
        .map((s: any) => {
          const price = Number(s.plan_prices?.price ?? 0);
          const amount = Math.max(0, price - Number(s.plan_discount ?? 0));
          if (amount <= 0) return null;
          const rel: any = s.parents;
          const parent: any = Array.isArray(rel) ? (rel[0] ?? null) : (rel ?? null);
          const cpfDigits = (parent?.cpf || "").replace(/\D/g, "");
          return {
            training_center_id: tcId,
            status: "aguardando",
            source: "contract",
            amount,
            payer_name: parent?.name ?? s.name ?? null,
            payer_cpf_middle: cpfDigits ? cpfDigits.slice(3, 9) : null,
            origin_phone: parent?.phone ?? null,
            match_level: "none",
            linked_kind: "student",
            linked_id: s.id,
            note: `Plano ${s.plan_prices?.plan_type ?? ""} — aguardando comprovante`,
          };
        })
        .filter((r): r is NonNullable<typeof r> => r !== null);
      if (rows.length > 0) {
        await context.supabase.from("pix_payment_proofs").insert(rows);
      }
    }

    // 4) Lê cobranças pendentes já reconciliadas
    const { data: proofs, error } = await context.supabase
      .from("pix_payment_proofs")
      .select("id, amount, status, note, origin_phone, payer_name, linked_id, linked_kind, created_at")
      .eq("training_center_id", tcId)
      .eq("source", "contract")
      .eq("linked_kind", "student")
      .in("status", view === "inativas" ? ["inativo"] : ["aguardando"])
      .order("created_at", { ascending: false });
    if (error) throw error;

    const byId = new Map((eligibleStudents ?? []).map((s: any) => [s.id, s]));

    /** Vencimento do plano: dia fixo, offset a partir da criação ou fim de vigência. */
    const contractDueDate = (s: any, createdAt: string): string | null => {
      const pp: any = s?.plan_prices;
      const created = new Date(createdAt);
      if (isNaN(created.getTime())) return s?.plan_end_date ?? null;
      if (pp?.due_type === "fixed_day" && pp?.due_fixed_day) {
        const day = Math.min(Number(pp.due_fixed_day) || 10, 28);
        const d = new Date(created.getFullYear(), created.getMonth(), day);
        if (d.getTime() < created.getTime()) d.setMonth(d.getMonth() + 1);
        return d.toISOString().slice(0, 10);
      }
      if (pp?.due_offset_days != null) {
        const d = new Date(created);
        d.setDate(d.getDate() + (Number(pp.due_offset_days) || 0));
        return d.toISOString().slice(0, 10);
      }
      return s?.plan_end_date ?? null;
    };

    const charges: any[] = (proofs ?? []).map((p: any) => {
      const s: any = byId.get(p.linked_id);
      const rel = s?.parents;
      const parent = Array.isArray(rel) ? rel[0] : rel;
      return {
        source: "contract",
        kind: "contract",
        id: p.id,
        amount: Number(p.amount ?? 0),
        status: p.status,
        note: p.note,
        created_at: p.created_at,
        due_date: contractDueDate(s, p.created_at),
        student_id: p.linked_id,
        student_name: s?.name ?? p.payer_name ?? "—",
        parent_name: parent?.name ?? null,
        phone: parent?.phone ?? p.origin_phone ?? null,
        plan_name: s?.plan_prices?.plan_type ?? null,
        payment_method: s?.payment_method ?? null,
      };
    });

    // 5) Vendas de uniformes/mercadorias (em aberto ou inativadas, conforme a visão)
    const applyOrderView = (q: any) =>
      view === "inativas"
        ? q.eq("status", "inativo")
        : q.not("status", "eq", "pago").not("status", "eq", "cancelado").not("status", "eq", "inativo");
    const [{ data: uOrders }, { data: pOrders }] = await Promise.all([
      applyOrderView(
        context.supabase
          .from("uniform_orders")
          .select("id,amount,discount,status,payment_method,due_date,created_at,buyer_name,buyer_phone,size,shirt_name,shirt_number,uniforms(name),students(id,name,parents(name,phone))")
          .eq("training_center_id", tcId),
      ),
      applyOrderView(
        context.supabase
          .from("product_orders")
          .select("id,amount,discount,quantity,status,payment_method,due_date,created_at,buyer_name,buyer_phone,products(name),students(id,name,parents(name,phone))")
          .eq("training_center_id", tcId),
      ),
    ]);


    for (const o of (uOrders ?? []) as any[]) {
      const parent = Array.isArray(o.students?.parents) ? o.students.parents[0] : o.students?.parents;
      const net = Math.max(0, Number(o.amount ?? 0) - Number(o.discount ?? 0));
      charges.push({
        source: "sale",
        kind: "uniform",
        id: o.id,
        amount: net,
        status: o.status,
        note: `${o.uniforms?.name ?? "Uniforme"}${o.size ? " — Tam. " + o.size : ""}${o.shirt_number ? " · Nº " + o.shirt_number : ""}${o.shirt_name ? " · " + o.shirt_name : ""}`,
        created_at: o.created_at,
        due_date: o.due_date ?? (o.created_at ? String(o.created_at).slice(0, 10) : null),
        student_id: o.students?.id ?? null,
        student_name: o.students?.name ?? o.buyer_name ?? "Avulso",
        parent_name: parent?.name ?? null,
        phone: parent?.phone ?? o.buyer_phone ?? null,
        plan_name: o.uniforms?.name ?? "Uniforme",
        payment_method: o.payment_method,
      });
    }
    for (const o of (pOrders ?? []) as any[]) {
      const parent = Array.isArray(o.students?.parents) ? o.students.parents[0] : o.students?.parents;
      const net = Math.max(0, Number(o.amount ?? 0) - Number(o.discount ?? 0));
      charges.push({
        source: "sale",
        kind: "product",
        id: o.id,
        amount: net,
        status: o.status,
        note: `${o.products?.name ?? "Mercadoria"}${o.quantity > 1 ? " · " + o.quantity + "×" : ""}`,
        created_at: o.created_at,
        due_date: o.due_date ?? (o.created_at ? String(o.created_at).slice(0, 10) : null),
        student_id: o.students?.id ?? null,
        student_name: o.students?.name ?? o.buyer_name ?? "Avulso",
        parent_name: parent?.name ?? null,
        phone: parent?.phone ?? o.buyer_phone ?? null,
        plan_name: o.products?.name ?? "Mercadoria",
        payment_method: o.payment_method,
      });
    }

    // 6) Reservas de espaço (locação) feitas pelo app do usuário
    const { data: rentalProofs } = await context.supabase
      .from("pix_payment_proofs")
      .select("id, amount, status, note, origin_phone, payer_name, linked_id, created_at")
      .eq("training_center_id", tcId)
      .eq("linked_kind", "rental")
      .in("status", view === "inativas" ? ["inativo"] : ["aguardando"]);

    const rentalIds = Array.from(new Set((rentalProofs ?? []).map((p: any) => p.linked_id).filter(Boolean)));
    let rentalById = new Map<string, any>();
    if (rentalIds.length > 0) {
      const { data: rentals } = await context.supabase
        .from("rentals")
        .select("id, start_at, end_at, payment_method, client_name, client_phone, rental_modalities(name)")
        .in("id", rentalIds);
      rentalById = new Map((rentals ?? []).map((r: any) => [r.id, r]));
    }

    for (const p of (rentalProofs ?? []) as any[]) {
      const r = rentalById.get(p.linked_id);
      const st = r?.start_at ? new Date(r.start_at) : null;
      const when = st
        ? `${st.toLocaleDateString("pt-BR")} ${st.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`
        : "";
      charges.push({
        source: "rental",
        kind: "rental",
        id: p.id,
        amount: Number(p.amount ?? 0),
        status: p.status,
        note: `${r?.rental_modalities?.name ?? "Reserva de espaço"}${when ? ` — ${when}` : ""}${p.note ? ` · ${p.note}` : ""}`,
        created_at: p.created_at,
        due_date: r?.start_at ? new Date(r.start_at).toISOString().slice(0, 10) : null,
        student_id: null,
        student_name: r?.client_name ?? p.payer_name ?? "Cliente",
        parent_name: r?.client_name ?? p.payer_name ?? null,
        phone: r?.client_phone ?? p.origin_phone ?? null,
        plan_name: "Reserva de espaço",
        payment_method: r?.payment_method ?? null,
      });
    }


    // 7) Taxas de eventos (source='fee') — geradas ao vincular a taxa ao evento
    const { data: feeProofs } = await context.supabase
      .from("pix_payment_proofs")
      .select("id, amount, status, note, origin_phone, payer_name, linked_id, created_at, payment_method")
      .eq("training_center_id", tcId)
      .eq("source", "fee")
      .eq("linked_kind", "student")
      .in("status", view === "inativas" ? ["inativo"] : ["aguardando"]);

    if ((feeProofs ?? []).length > 0) {
      const { data: feeRows } = await context.supabase
        .from("fees")
        .select("id, name, type, amount, due_date, payment_method, championships(name)")
        .eq("training_center_id", tcId);

      const studentIds = Array.from(new Set((feeProofs ?? []).map((p: any) => p.linked_id).filter(Boolean)));
      let studentMap = new Map<string, any>();
      if (studentIds.length > 0) {
        const { data: sts } = await context.supabase
          .from("students")
          .select("id, name, parents(name, phone)")
          .in("id", studentIds);
        studentMap = new Map((sts ?? []).map((s: any) => [s.id, s]));
      }

      for (const p of (feeProofs ?? []) as any[]) {
        const fee: any = (feeRows ?? []).find((f: any) => (p.note ?? "").includes(`[fee:${f.id}]`));
        const s: any = studentMap.get(p.linked_id);
        const rel = s?.parents;
        const parent = Array.isArray(rel) ? rel[0] : rel;
        charges.push({
          source: "fee",
          kind: "fee",
          id: p.id,
          amount: Number(p.amount ?? 0),
          status: p.status,
          note: `${fee?.name ?? "Taxa"}${fee?.championships?.name ? ` — ${fee.championships.name}` : ""}`,
          created_at: p.created_at,
          due_date: fee?.due_date ?? null,
          student_id: p.linked_id,
          student_name: s?.name ?? p.payer_name ?? "—",
          parent_name: parent?.name ?? p.payer_name ?? null,
          phone: parent?.phone ?? p.origin_phone ?? null,
          plan_name: fee?.name ?? "Taxa",
          payment_method: p.payment_method ?? fee?.payment_method ?? null,
        });
      }
    }

    charges.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return { charges };
  });

export const updateCharge = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      id: z.string().uuid(),
      amount: z.coerce.number().min(0),
      note: z.string().nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("pix_payment_proofs")
      .update({ amount: data.amount, note: data.note ?? null })
      .eq("id", data.id)
      .eq("training_center_id", tcId);
    if (error) throw error;
    return { ok: true };
  });

/**
 * Define a forma de pagamento de UMA cobrança (override individual).
 * Aceita o id da cobrança (pix_payment_proofs) OU o par taxa+aluno, usado
 * pela convocação e pelo envio de cobrança por WhatsApp.
 */
export const updateChargePaymentMethod = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      id: z.string().uuid().optional(),
      fee_id: z.string().uuid().optional(),
      student_id: z.string().uuid().optional(),
      payment_method: z.string().min(1),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);

    let chargeId = data.id ?? null;
    if (!chargeId && data.fee_id && data.student_id) {
      const { data: rows } = await context.supabase
        .from("pix_payment_proofs")
        .select("id, note")
        .eq("training_center_id", tcId)
        .eq("source", "fee")
        .eq("linked_kind", "student")
        .eq("linked_id", data.student_id)
        .in("status", ["aguardando", "inativo"]);
      const hit = (rows ?? []).find((r: any) => (r.note ?? "").includes(`[fee:${data.fee_id}]`));
      chargeId = (hit as any)?.id ?? null;
    }
    if (!chargeId) return { ok: false as const, notFound: true as const };

    const { error } = await context.supabase
      .from("pix_payment_proofs")
      .update({ payment_method: data.payment_method })
      .eq("id", chargeId)
      .eq("training_center_id", tcId);
    if (error) throw error;
    return { ok: true as const, id: chargeId };
  });


export const getBillingPixInfo = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    // pix_key/pix_beneficiary are sensitive; use admin client scoped by verified owner tcId
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("training_centers")
      .select("id, name, city, pix_key, pix_beneficiary")
      .eq("id", tcId)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error("Centro de Treinamento não encontrado.");
    return {
      id: data.id,
      name: data.name,
      city: (data as any).city ?? null,
      pix_key: (data as any).pix_key ?? null,
      pix_beneficiary: (data as any).pix_beneficiary ?? null,
    };
  });

/**
 * Endpoint público (sem auth) usado pela página /pix/$id.
 * Retorna apenas campos seguros para o pagador.
 */
export const getPublicChargeInfo = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { buildPixBrCode } = await import("@/lib/pix/brcode");

    const { data: proof, error } = await supabaseAdmin
      .from("pix_payment_proofs")
      .select("id, amount, status, linked_id, linked_kind, training_center_id")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw error;
    if (!proof) throw new Error("Cobrança não encontrada.");

    const { data: tc } = await supabaseAdmin
      .from("training_centers")
      .select("id, name, city, pix_key, pix_beneficiary")
      .eq("id", proof.training_center_id)
      .maybeSingle();
    if (!tc) throw new Error("CT não encontrado.");

    let studentFirstName: string | null = null;
    if (proof.linked_kind === "student" && proof.linked_id) {
      const { data: s } = await supabaseAdmin
        .from("students")
        .select("name")
        .eq("id", proof.linked_id)
        .maybeSingle();
      studentFirstName = (s?.name || "").split(" ")[0] || null;
    }

    const amount = Number(proof.amount ?? 0);
    const brcode = tc.pix_key
      ? buildPixBrCode({
          pixKey: tc.pix_key,
          beneficiaryName: tc.pix_beneficiary || tc.name || "PAGADOR",
          city: tc.city || "BRASIL",
          amount: amount > 0 ? amount : undefined,
          txid: proof.id.replace(/-/g, "").slice(0, 25),
        })
      : "";

    return {
      id: proof.id,
      status: proof.status,
      amount,
      ct_name: tc.name,
      pix_key: tc.pix_key ?? null,
      pix_beneficiary: tc.pix_beneficiary ?? null,
      brcode,
      student_first_name: studentFirstName,
    };
  });


/**
 * Inativa (ou reativa) uma cobrança — mensalidade de contrato ou venda.
 * Cobranças inativas somem da lista de pendentes, mas continuam registradas
 * como débito do aluno/responsável e aparecem no filtro "Inativas".
 */
export const setChargeActive = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      id: z.string().uuid(),
      source: z.enum(["contract", "sale", "rental", "fee"]),
      kind: z.enum(["contract", "uniform", "product", "rental", "fee"]),
      active: z.boolean(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    const table =
      data.source === "contract" || data.source === "rental" || data.source === "fee"
        ? "pix_payment_proofs"
        : data.kind === "uniform"
          ? "uniform_orders"
          : "product_orders";
    const nextStatus = data.active
      ? data.source === "contract" || data.source === "rental" || data.source === "fee"
        ? "aguardando"
        : "pendente"
      : "inativo";
    const { error } = await context.supabase
      .from(table)
      .update({ status: nextStatus })
      .eq("id", data.id)
      .eq("training_center_id", tcId);
    if (error) throw error;
    return { ok: true, status: nextStatus };
  });

/**
 * Consulta débitos (pendentes ou inativados) vinculados a um CPF —
 * seja de aluno ou de responsável — dentro do CT logado.
 * Usado para avisar no cadastro de um novo aluno.
 */
export const checkDebtByCpf = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ cpf: z.string().min(11).max(20) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    const digits = data.cpf.replace(/\D/g, "");
    if (digits.length !== 11) return { hasDebt: false, total: 0, items: [] as any[] };

    const masked = `%${digits.slice(0, 3)}%${digits.slice(3, 6)}%${digits.slice(6, 9)}%${digits.slice(9)}%`;

    const [{ data: byStudent }, { data: byParent }] = await Promise.all([
      context.supabase
        .from("students")
        .select("id,name,cpf")
        .eq("training_center_id", tcId)
        .or(`cpf.eq.${digits},cpf.ilike.${masked}`),
      context.supabase
        .from("parents")
        .select("id,name,cpf,students(id,name)")
        .eq("training_center_id", tcId)
        .or(`cpf.eq.${digits},cpf.ilike.${masked}`),
    ]);

    const studentIds = new Set<string>();
    const nameById = new Map<string, string>();
    for (const s of (byStudent ?? []) as any[]) {
      studentIds.add(s.id);
      nameById.set(s.id, s.name);
    }
    for (const p of (byParent ?? []) as any[]) {
      for (const s of (p.students ?? []) as any[]) {
        studentIds.add(s.id);
        nameById.set(s.id, s.name);
      }
    }
    if (studentIds.size === 0) return { hasDebt: false, total: 0, items: [] as any[] };

    const ids = [...studentIds];
    const openStatuses = ["aguardando", "inativo", "pendente"];

    const [{ data: proofs }, { data: uOrders }, { data: pOrders }] = await Promise.all([
      context.supabase
        .from("pix_payment_proofs")
        .select("id,amount,status,note,linked_id")
        .eq("training_center_id", tcId)
        .eq("linked_kind", "student")
        .in("linked_id", ids)
        .in("status", ["aguardando", "inativo"]),
      context.supabase
        .from("uniform_orders")
        .select("id,amount,discount,status,student_id,uniforms(name)")
        .eq("training_center_id", tcId)
        .in("student_id", ids)
        .in("status", openStatuses),
      context.supabase
        .from("product_orders")
        .select("id,amount,discount,status,student_id,products(name)")
        .eq("training_center_id", tcId)
        .in("student_id", ids)
        .in("status", openStatuses),
    ]);

    const items: any[] = [];
    for (const p of (proofs ?? []) as any[]) {
      items.push({
        id: p.id,
        kind: "Mensalidade",
        description: p.note ?? "Mensalidade",
        student_name: nameById.get(p.linked_id) ?? "—",
        amount: Number(p.amount ?? 0),
        status: p.status,
      });
    }
    for (const o of (uOrders ?? []) as any[]) {
      items.push({
        id: o.id,
        kind: "Uniforme",
        description: o.uniforms?.name ?? "Uniforme",
        student_name: nameById.get(o.student_id) ?? "—",
        amount: Math.max(0, Number(o.amount ?? 0) - Number(o.discount ?? 0)),
        status: o.status,
      });
    }
    for (const o of (pOrders ?? []) as any[]) {
      items.push({
        id: o.id,
        kind: "Mercadoria",
        description: o.products?.name ?? "Mercadoria",
        student_name: nameById.get(o.student_id) ?? "—",
        amount: Math.max(0, Number(o.amount ?? 0) - Number(o.discount ?? 0)),
        status: o.status,
      });
    }

    const total = items.reduce((s, i) => s + i.amount, 0);
    return { hasDebt: items.length > 0, total, items };
  });

/**
 * Quita manualmente uma cobrança de comprovante (contrato, taxa ou locação):
 * marca como confirmada e lança a entrada no caixa (idempotente).
 * Com `exempt: true` a cobrança é isentada (valor 0, sem lançamento no caixa).
 */
export const settleChargeManually = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      id: z.string().uuid(),
      payment_method: z.string().min(1).default("dinheiro"),
      exempt: z.boolean().optional().default(false),
      /** Desconto aplicado sobre o valor devido (R$). */
      discount: z.coerce.number().min(0).optional().default(0),
      /** Valor efetivamente recebido agora. Menor que o devido = pagamento parcial. */
      paid_amount: z.coerce.number().min(0).nullable().optional(),
      note: z.string().max(300).nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    const { resolveActor } = await import("@/lib/api/actor.server");
    const actor = await resolveActor(context.supabase, context.userId);

    const { data: proof, error } = await context.supabase
      .from("pix_payment_proofs")
      .select("id, amount, note, source, linked_id, linked_kind, status")
      .eq("id", data.id)
      .eq("training_center_id", tcId)
      .maybeSingle();
    if (error) throw error;
    if (!proof) throw new Error("Cobrança não encontrada.");
    if (proof.status === "confirmado") return { ok: true, alreadySettled: true };

    let studentName: string | null = null;
    if (proof.linked_kind === "student" && proof.linked_id) {
      const { data: s } = await context.supabase
        .from("students").select("name").eq("id", proof.linked_id).maybeSingle();
      studentName = s?.name ?? null;
    }

    const baseNote = data.note ?? proof.note ?? "";
    const isFee = proof.source === "fee";
    const actorTag = actor.name ? ` por ${actor.name}` : "";

    const insertCash = async (value: number, extra: string) => {
      if (value <= 0) return;
      const { error: cErr } = await context.supabase.from("cash_movements").insert({
        training_center_id: tcId,
        kind: "entrada",
        description: `${isFee ? "Taxa" : "Cobrança"}${studentName ? " — " + studentName : ""}${extra}${baseNote ? " · " + baseNote.replace(/\[fee:[^\]]+\]\s*/g, "") : ""}`,
        amount: value,
        payment_method: data.payment_method,
        category: isFee ? "Taxa" : "Mensalidade",
        source_table: "pix_payment_proofs",
        source_id: proof.id,
        created_by: actor.id,
        created_by_name: actor.name,
      } as any);
      if (cErr) throw cErr;
    };

    if (data.exempt) {
      const { error: uErr } = await context.supabase
        .from("pix_payment_proofs")
        .update({ amount: 0, status: "confirmado", note: `${baseNote} [isento${actorTag}]`.trim() })
        .eq("id", proof.id)
        .eq("training_center_id", tcId);
      if (uErr) throw uErr;
      if (isFee) {
        const { onFeeChargeConfirmed } = await import("@/lib/api/summon-notify.server");
        await onFeeChargeConfirmed(context.supabase, tcId, { ...(proof as any), amount: 0 });
      }
      return { ok: true, exempt: true };
    }

    const due = Number(proof.amount ?? 0);
    const discount = Math.min(Number(data.discount ?? 0), due);
    const effective = Math.max(0, due - discount);
    const paid = data.paid_amount === null || data.paid_amount === undefined
      ? effective
      : Math.min(Number(data.paid_amount), effective);

    // ---- Pagamento parcial: mantém a cobrança aberta com o saldo restante ----
    if (paid > 0 && paid < effective) {
      const remaining = Number((effective - paid).toFixed(2));
      const { error: uErr } = await context.supabase
        .from("pix_payment_proofs")
        .update({
          amount: remaining,
          note: `${baseNote} [pagamento parcial ${paid.toFixed(2)} de ${effective.toFixed(2)}${discount > 0 ? ` · desconto ${discount.toFixed(2)}` : ""}${actorTag}]`.trim(),
        })
        .eq("id", proof.id)
        .eq("training_center_id", tcId);
      if (uErr) throw uErr;
      await insertCash(paid, " (parcial)");
      return { ok: true, partial: true, remaining };
    }

    const { error: uErr } = await context.supabase
      .from("pix_payment_proofs")
      .update({
        amount: effective,
        status: "confirmado",
        note: `${baseNote} [quitado manualmente: ${data.payment_method}${discount > 0 ? ` · desconto ${discount.toFixed(2)}` : ""}${actorTag}]`.trim(),
      })
      .eq("id", proof.id)
      .eq("training_center_id", tcId);
    if (uErr) throw uErr;

    if (effective > 0) {
      const { data: existing } = await context.supabase
        .from("cash_movements")
        .select("id")
        .eq("source_table", "pix_payment_proofs")
        .eq("source_id", proof.id)
        .maybeSingle();
      if (!existing) await insertCash(effective, discount > 0 ? " (com desconto)" : "");
    }

    // Taxa quitada → confirma presença na convocação e avisa o responsável.
    if (isFee) {
      const { onFeeChargeConfirmed } = await import("@/lib/api/summon-notify.server");
      await onFeeChargeConfirmed(context.supabase, tcId, { ...(proof as any), amount: effective });
    }

    return { ok: true };
  });



/** Receita consolidada das taxas de evento (cobranças source='fee' confirmadas). */
export const listFeeRevenue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      start: z.string().optional().nullable(),
      end: z.string().optional().nullable(),
      championship_id: z.string().uuid().optional().nullable(),
    }).parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);
    const start = data.start || new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    const end = data.end || new Date().toISOString().slice(0, 10);

    const { data: proofs, error } = await context.supabase
      .from("pix_payment_proofs")
      .select("id, amount, status, note, linked_id, created_at")
      .eq("training_center_id", tcId)
      .eq("source", "fee")
      .gte("created_at", `${start}T00:00:00`)
      .lte("created_at", `${end}T23:59:59`);
    if (error) throw error;

    const { data: fees } = await context.supabase
      .from("fees")
      .select("id, name, amount, type, championship_id, championships(name)")
      .eq("training_center_id", tcId);

    const feeList = (fees ?? []).filter(
      (f: any) => !data.championship_id || f.championship_id === data.championship_id,
    );

    const studentIds = Array.from(new Set((proofs ?? []).map((p: any) => p.linked_id).filter(Boolean)));
    let studentMap = new Map<string, string>();
    if (studentIds.length > 0) {
      const { data: sts } = await context.supabase
        .from("students").select("id, name").in("id", studentIds);
      studentMap = new Map((sts ?? []).map((s: any) => [s.id, s.name]));
    }

    const rows: any[] = [];
    for (const p of (proofs ?? []) as any[]) {
      const fee: any = feeList.find((f: any) => (p.note ?? "").includes(`[fee:${f.id}]`));
      if (!fee) continue;
      rows.push({
        id: p.id,
        fee_id: fee.id,
        fee_name: fee.name,
        fee_type: fee.type,
        event_name: fee.championships?.name ?? null,
        student_name: studentMap.get(p.linked_id) ?? "—",
        amount: Number(p.amount ?? 0),
        status: p.status,
        exempt: (p.note ?? "").includes("[isento]"),
        created_at: p.created_at,
      });
    }

    const received = rows.filter((r) => r.status === "confirmado").reduce((s, r) => s + r.amount, 0);
    const pending = rows.filter((r) => r.status === "aguardando").reduce((s, r) => s + r.amount, 0);
    rows.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return { rows, received, pending, start, end };
  });

/**
 * Relação de taxas × atletas convocados de um evento, com o status de
 * pagamento de cada cobrança. Usado no diálogo "Verificar e quitar Taxas".
 */
export const listEventFeeCharges = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ championship_id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const tcId = await resolveMyTcId(context.supabase, context.userId);

    // garante que as cobranças das taxas do evento existem para os convocados
    try {
      const { syncFeeChargesForEvent } = await import("@/lib/api/fee-charges.server");
      await syncFeeChargesForEvent(context.supabase, tcId, data.championship_id);
    } catch { /* silent */ }

    const { data: fees } = await context.supabase
      .from("fees")
      .select("id, name, type, amount, payment_method, due_date")
      .eq("training_center_id", tcId)
      .eq("championship_id", data.championship_id);

    const feeIds = (fees ?? []).map((f: any) => f.id);
    if (feeIds.length === 0) return { fees: [], rows: [] as any[] };

    const { data: proofs } = await context.supabase
      .from("pix_payment_proofs")
      .select("id, amount, status, note, linked_id, created_at")
      .eq("training_center_id", tcId)
      .eq("source", "fee")
      .eq("linked_kind", "student");

    const studentIds = Array.from(new Set((proofs ?? []).map((p: any) => p.linked_id).filter(Boolean)));
    let studentMap = new Map<string, any>();
    if (studentIds.length > 0) {
      const { data: sts } = await context.supabase
        .from("students")
        .select("id, name, category, parents(name, phone)")
        .in("id", studentIds);
      studentMap = new Map((sts ?? []).map((s: any) => [s.id, s]));
    }

    const rows: any[] = [];
    for (const p of (proofs ?? []) as any[]) {
      const fee: any = (fees ?? []).find((f: any) => (p.note ?? "").includes(`[fee:${f.id}]`));
      if (!fee) continue;
      const s: any = studentMap.get(p.linked_id);
      const rel = s?.parents;
      const parent = Array.isArray(rel) ? rel[0] : rel;
      rows.push({
        id: p.id,
        fee_id: fee.id,
        fee_name: fee.name,
        fee_amount: Number(fee.amount ?? 0),
        payment_method: fee.payment_method ?? null,
        student_id: p.linked_id,
        student_name: s?.name ?? "—",
        category: s?.category ?? null,
        parent_name: parent?.name ?? null,
        phone: parent?.phone ?? null,
        amount: Number(p.amount ?? 0),
        status: p.status,
        exempt: (p.note ?? "").includes("[isento]"),
      });
    }
    rows.sort((a, b) => a.fee_name.localeCompare(b.fee_name) || a.student_name.localeCompare(b.student_name));
    return { fees: fees ?? [], rows };
  });
